;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:locale\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:slug*\",\"regex\":\"^/([^/]+)/(.+)$\",\"paramNames\":[\"locale\",\"slug\"],\"hasOptionalPrefix\":true}],\"staticRoutes\":[]}";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";globalThis["_sentryTunnelRouteMode"] = "rewrite";globalThis["_sentryNextJsVersion"] = "16.4.3";import { analyticsEnvironment } from '@/config/analytics'
import { BENIGN_EXCEPTIONS } from '@/lib/analytics/benign-exceptions'
import { analytics, dropBenignExceptions, initPostHog } from '@/lib/analytics/posthog'
import * as Sentry from '@sentry/nextjs'

/**
 * Whether this page is a deployment rather than somebody's laptop.
 *
 * `next start` sets `NODE_ENV=production`, so a local production build is indistinguishable from a
 * real one by environment alone — which is how the Playwright web server on `127.0.0.1:3100` and a
 * hand-started one on `:3101` both filed errors into the **production** Sentry project, tagged
 * `environment: production`. Two issues in two days, six "users", none of them a visitor.
 *
 * The host is the honest discriminator: a deployment answers on a real domain. Preview and branch
 * deployments still report, which is the point — they are real servers that real people open.
 */
function isDeployedHost(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const { hostname } = window.location

  return hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '[::1]' && !hostname.endsWith('.test')
}

if (isDeployedHost()) {
  // No `sendDefaultPii` and no `enableLogs`: Sentry v11 removed both. Its `dataCollection` defaults
  // collect what `sendDefaultPii: true` used to (user info, cookies, headers), and logs are captured
  // only when `Sentry.logger` or a logging integration is used, which this site does neither of.
  //
  // Except bodies: v11 also collects every HTTP request and response body by default, and the bodies
  // this site handles are the register form's (a plaintext password) and the contact form's (a
  // person's details). An error during one of those requests would carry them to Sentry. `[]` turns
  // body collection off; nothing else changes.
  Sentry.init({
    dsn: 'https://d302be155a357d30114c5732daad603e@o69887.ingest.us.sentry.io/4510782909317120',
    // The same list PostHog's `before_send` drops, so the two trackers agree on what is noise.
    ignoreErrors: BENIGN_EXCEPTIONS,
    dataCollection: { httpBodies: [] },
  })
}

const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN

if (posthogToken) {
  initPostHog(posthogToken, {
    // Route ingestion through the same-origin reverse proxy (see next.config.ts)
    api_host: '/ingest',
    ui_host: 'https://eu.posthog.com',
    // Required by PostHog, and pinned rather than chased.
    //
    // The date selects which behaviour changes the SDK applies by default. Every one gated behind
    // a later date than this — `strictMinimumDuration`, `canvasCapture.resolutionScale`,
    // `streamNetworkBody`, `captureJsonLd`, checked against 1.434.8 — configures **session
    // replay**, and replay is off on this project (`session_recording_opt_in: false`, read
    // 2026-09-23). So moving it forward would change nothing here today, and moving it forward on
    // the day replay is switched on is a decision about replay, not about an SDK upgrade.
    defaults: '2026-01-30',
    // Turn off the "autotracking" layer (DOM-interaction noise) while keeping the signal we rely
    // on. We capture our own named funnel events explicitly, so autocapture ($autocapture clicks/
    // changes/submits, rageclicks), dead clicks, and web-vitals only add volume without analytical
    // value here. Pageviews + pageleaves stay on: web analytics, channel/referrer attribution, and
    // bounce/session metrics all depend on them.
    autocapture: false,
    capture_dead_clicks: false,
    capture_performance: { web_vitals: false },
    // Heatmaps stay on — for a landing page, click/scroll heatmaps drive CRO, and (unlike
    // autocapture) heatmap data is a separate stream that does NOT create per-interaction events.
    capture_heatmaps: true,
    // Nothing on this site runs a PostHog survey, and leaving it on costs a ~96 KB bundle on
    // every page that nothing ever reads.
    disable_surveys: true,
    // Enables capturing unhandled exceptions via Error Tracking
    capture_exceptions: true,
    // …minus the browser warnings that are not faults (`dropBenignExceptions` says which, and why).
    before_send: dropBenignExceptions,
    // Create a person only once we identify (by email). Anonymous pre-identify events (pageviews
    // carrying the campaign attribution) merge onto that person when identify() is called.
    person_profiles: 'identified_only',
    // Attribution is the north star: persist UTMs + ad click ids so every conversion — including
    // revenue captured later by the backend — can be traced back to its campaign/channel. PostHog
    // stores each one twice on the person: the first touch as `$initial_<name>`, and the latest
    // under the bare name.
    save_campaign_params: true,
    // Every one of these is already in posthog-js's own default list (re-checked against 1.434.8,
    // which ships gclid, gclsrc, dclid, gbraid, wbraid, fbclid, msclkid, twclid, li_fat_id, igshid,
    // ttclid, rdt_cid, epik, qclid, sccid, irclid, _kx, gad_source and mc_cid — `irclid` and `_kx`
    // joined since 1.409.0). Listing them anyway is deliberate rather
    // than redundant: it states which parameters this site depends on, so a future release of the
    // SDK dropping one from its defaults cannot quietly stop attribution that the Manager's
    // `ClientAttribution` columns and the ad destinations both read.
    //
    // Note what this does *not* do. These are read from the query string only. LinkedIn's
    // `li_fat_id` is also written to a first-party cookie by its Insight Tag, and only the cookie
    // carries a view-through — so the Manager reads that one from the cookie as well
    // (`App\Support\RequestData`), which is the half that matters most for LinkedIn.
    custom_campaign_params: ['gbraid', 'wbraid', 'ttclid', 'twclid', 'li_fat_id', 'epik', 'gad_source'],
    // Turn on debug in development mode
    debug: process.env.NODE_ENV === 'development',
  })

  // Tag every event with the deployment environment, so preview and local traffic can be kept out
  // of this site's own dashboards. It is not a destination filter — those go on event name and on
  // `source`, never on `environment`; see the note in config/analytics.ts.
  //
  // Queued until the SDK lands, and the queue drains in order — so this is registered before the
  // first capture leaves, exactly as it was when the init ran inline. Putting the singleton on
  // `window` now belongs to `initPostHog`, which is the only code that has it.
  analytics.register({ environment: analyticsEnvironment })
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
